import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useTranslation } from "@/features/utils/i18n";
import { QuestionMarkIcon } from "@phosphor-icons/react";

interface FilterSyntaxExample {
  pattern: string;
  /** Key under `table.filterHelp`. */
  description: string;
  example: string;
  /** One tone per family of operator: equality, comparison, negation, match. */
  color: string;
}

const filterExamples: FilterSyntaxExample[] = [
  {
    pattern: "column=value",
    description: "exact",
    example: "user_id=abc123",
    color: "text-info-foreground",
  },
  {
    pattern: 'column="quoted value"',
    description: "exactQuoted",
    example: 'name="John Doe"',
    color: "text-info-foreground",
  },
  {
    pattern: "column>value",
    description: "greater",
    example: "amount>100",
    color: "text-success-foreground",
  },
  {
    pattern: "column>=value",
    description: "greaterOrEqual",
    example: "created_at>=1672531200",
    color: "text-success-foreground",
  },
  {
    pattern: "column<value",
    description: "less",
    example: "balance<50",
    color: "text-success-foreground",
  },
  {
    pattern: "column<=value",
    description: "lessOrEqual",
    example: "age<=25",
    color: "text-success-foreground",
  },
  {
    pattern: "column!=value",
    description: "notEqual",
    example: "status!=pending",
    color: "text-danger-foreground",
  },
  {
    pattern: "column~value",
    description: "contains",
    example: "email~gmail.com",
    color: "text-warning-foreground",
  },
];

/**
 * The `key=value` syntax parsed by `parseSearchFilters`. Only meaningful where
 * the search goes to the server: DataTable renders it on those tables only.
 */
export function SearchFilterHelp() {
  const { t } = useTranslation("table");
  if (true) return null; // Hide for now as the feature is not fully ready
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
          aria-label={t("table.filterHelp.open")}
        >
          <QuestionMarkIcon className="h-4 w-4" weight="fill" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-96 max-w-[calc(100vw-2rem)]" align="start">
        <div className="space-y-3">
          <div>
            <h4 className="font-semibold text-sm mb-2">
              {t("table.filterHelp.title")}
            </h4>
            <p className="text-xs text-muted-foreground mb-3">
              {t("table.filterHelp.intro")}
            </p>
          </div>

          <div className="space-y-2 max-h-64 overflow-y-auto">
            {filterExamples.map((example) => (
              <div
                key={example.pattern}
                className="flex items-start gap-2 text-xs"
              >
                <code
                  className={`font-mono px-1.5 py-0.5 bg-muted rounded-xs text-xs ${example.color} whitespace-nowrap`}
                >
                  {example.pattern}
                </code>
                <div className="flex-1 min-w-0">
                  <div className="font-medium">
                    {t(`table.filterHelp.${example.description}`)}
                  </div>
                  <div className="text-muted-foreground">
                    {t("table.filterHelp.example")}{" "}
                    <code className="font-mono">{example.example}</code>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="pt-2 border-t text-xs text-muted-foreground">
            <p>{t("table.filterHelp.tip")}</p>
            <code className="block mt-1 p-1.5 bg-muted rounded-sm font-mono text-xs">
              status=active amount&gt;100 john doe
            </code>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
